import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MasonryGrid, type Photo } from "../components/portfolio/MasonryGrid";
import { Lightbox } from "../components/portfolio/Lightbox";
import { Button } from "../components/common/Button";
import { getTopRankedImages } from "../data/projectLoader";
import styles from "./Home.module.css";

export const Home: React.FC = () => {
  const [recentPhotos, setRecentPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  useEffect(() => {
    const loadRecentWork = async () => {
      setLoading(true);
      try {
        const rankedPhotos = await getTopRankedImages(12);
        setRecentPhotos(rankedPhotos);
      } catch (error) {
        console.error("Failed to load recent work:", error);
      } finally {
        setLoading(false);
      }
    };

    loadRecentWork();
  }, []);

  const handlePhotoClick = (photo: Photo) => {
    const index = recentPhotos.findIndex((p) => p.id === photo.id);
    setCurrentIndex(index);
    setSelectedPhoto(photo);
  };

  const handleClose = () => {
    setSelectedPhoto(null);
  };

  const handleNext = () => {
    if (recentPhotos.length === 0) return;
    const nextIndex = (currentIndex + 1) % recentPhotos.length;
    setCurrentIndex(nextIndex);
    setSelectedPhoto(recentPhotos[nextIndex]);
  };

  const handlePrev = () => {
    if (recentPhotos.length === 0) return;
    const prevIndex =
      (currentIndex - 1 + recentPhotos.length) % recentPhotos.length;
    setCurrentIndex(prevIndex);
    setSelectedPhoto(recentPhotos[prevIndex]);
  };

  return (
    <div className={styles.home}>
      {/* Recent Work Gallery */}
      <section className={styles.recentWork}>
        {loading ? (
          <div className={styles.loading}>
            <div className={styles.loadingSpinner} />
            <p>Loading recent work...</p>
          </div>
        ) : recentPhotos.length === 0 ? (
          <div className={styles.emptyState}>
            <p>No recent work available. Check back soon!</p>
          </div>
        ) : (
          <MasonryGrid
            photos={recentPhotos}
            columns={4}
            staggerDelay={60}
            onPhotoClick={handlePhotoClick}
          />
        )}

        <div className={styles.viewAllContainer}>
          <Link to="/portfolio">
            <Button variant="primary" size="large">
              View Full Portfolio
            </Button>
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaContent}>
          <h2 className={styles.ctaTitle}>Ready to Capture Your Story?</h2>
          <p className={styles.ctaText}>
            Let's create something beautiful together
          </p>
          <Link to="/contact">
            <Button variant="primary" size="large">
              Get in Touch
            </Button>
          </Link>
        </div>
      </section>

      {/* Lightbox */}
      {selectedPhoto && (
        <Lightbox
          photo={selectedPhoto}
          onClose={handleClose}
          onNext={handleNext}
          onPrev={handlePrev}
        />
      )}
    </div>
  );
};
